import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import MusicNoteOutlinedIcon from "@mui/icons-material/MusicNoteOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import ImageSearchOutlinedIcon from "@mui/icons-material/ImageSearchOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import classNames from "classnames";
import { DragEvent, MouseEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { LanguageData, selectCurrentLanguage, selectLocalizedLanguages } from "../../app/slices/TrainingLocalizationSlice";
import { selectReferenceLanguage } from "../../app/slices/TrainingSlice";
import PiPopoverMenu from "../../components/PiPopoverMenu";
import { AssetKey, AssetVariantDTO } from "../../dto/AssetDTO";
import { EditorColumnRow } from "../editor_columns/EditorColumn";
import PiMenuButton from "../menu/PiMenuButton";
import { languageCodesEqual } from "../variants/languageCode";
import { DEFAULT_VARIANT } from "../variants/resolveVariant";
import { useAssetVariants } from "./useAssetVariants";
import { LanguageUpload, useLanguageUploads } from "./useLanguageUploads";
import { useVariantPlayer } from "./useVariantPlayer";
import { useWindowFileDrag } from "./useWindowFileDrag";

type FileIcon = "video" | "audio" | "pdf";

/**
 * The extensions the API accepts per asset kind (Creator-Api `AssetKey.cs`). Checked by extension, not
 * MIME type: Windows browsers report .ogg as video/ogg, application/ogg or nothing at all.
 */
const EXTENSIONS: Partial<Record<AssetKey, string[]>> = {
  [AssetKey.Image]: [".png", ".jpg", ".jpeg"],
  [AssetKey.Video]: [".mp4", ".mov", ".webm"],
  [AssetKey.Audio]: [".ogg", ".mp3", ".wav"],
  [AssetKey.Pdf]: [".pdf"],
};
const extensionsFor = (key: AssetKey | undefined) => EXTENSIONS[key ?? AssetKey.Image] ?? EXTENSIONS[AssetKey.Image]!;
const acceptsFile = (key: AssetKey | undefined, file: File) =>
  extensionsFor(key).some((extension) => file.name.toLowerCase().endsWith(extension));

interface AssetLanguageListProps {
  assetId: string | undefined;
  /** "wide" for 360 images (2:1), "square" for answer images. */
  thumbnail?: "wide" | "square";
  /** Pick a different asset altogether (the original row's menu, or its empty box). */
  onChangeOriginal?: () => void;
  changeOriginalLabel?: string;
  testId?: string;
}

/** Where a picked file goes: a language's new variant, or a replacement for its existing one. */
interface UploadTarget {
  languageCode: string;
  replacing?: AssetVariantDTO;
}

/**
 * The original ("default" — not a language) on top, then one row per language added to the training,
 * reference language first. A language with its own variant shows it; one without plays the original,
 * so its box shows the original image with a dashed outline — an invitation, never an error. Clicking
 * that box (file picker) or dropping a file on the row adds the language's own image. The
 * project language's row is selected, the same way the preview follows it.
 */
export default function AssetLanguageList(props: AssetLanguageListProps) {
  const { assetId, thumbnail = "wide", onChangeOriginal, changeOriginalLabel, testId } = props;
  const { t } = useTranslation();
  const languages = useSelector(selectLocalizedLanguages);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const referenceLanguage = useSelector(selectReferenceLanguage);
  const asset = useAssetVariants(assetId);
  const variants = asset?.variants ?? [];
  const { uploads, upload, fail } = useLanguageUploads(asset);
  const isDraggingFile = useWindowFileDrag();

  const fileInput = useRef<HTMLInputElement>(null);
  const [pendingTarget, setPendingTarget] = useState<UploadTarget>();
  const [dropRow, setDropRow] = useState<string>();
  // A drag can end without a dragleave on the row (Esc, a drop outside the window): clear the row then.
  useEffect(() => {
    if (!isDraggingFile) setDropRow(undefined);
  }, [isDraggingFile]);

  const accept = extensionsFor(asset?.key).join(",");
  const accepts = (file: File) => acceptsFile(asset?.key, file);
  // Video, audio and PDF have no picture to show: their rows show an icon, and audio rows can be listened to.
  const fileIcon: FileIcon | undefined =
    asset?.key === AssetKey.Video ? "video" : asset?.key === AssetKey.Audio ? "audio" : asset?.key === AssetKey.Pdf ? "pdf" : undefined;
  const player = useVariantPlayer(fileIcon === "audio" ? assetId : undefined);
  const playButton = (variant: AssetVariantDTO | undefined) =>
    fileIcon === "audio" && variant ? (
      <PlayButton playing={player.playingId === variant.id} onToggle={() => player.toggle(variant)} t={t} />
    ) : null;

  const originalVariant = variants.find((v) => v.variant === DEFAULT_VARIANT) ?? variants[0];
  const ownVariant = (code: string) => variants.find((v) => languageCodesEqual(v.variant, code));

  const chooseFile = (target: UploadTarget) => {
    setPendingTarget(target);
    fileInput.current?.click();
  };

  const onFilePicked = (file: File | undefined) => {
    if (!file || !pendingTarget) return;
    if (!accepts(file)) fail(pendingTarget.languageCode, "type");
    else upload(pendingTarget.languageCode, file, pendingTarget.replacing);
    setPendingTarget(undefined);
  };

  // Dropping on a language that already has its own image replaces it.
  const dropProps = (languageCode: string, replacing?: AssetVariantDTO) => ({
    onDragOver: (event: DragEvent) => {
      if (!Array.from(event.dataTransfer.types).includes("Files")) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      if (dropRow !== languageCode) setDropRow(languageCode);
    },
    onDragLeave: (event: DragEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropRow(undefined);
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault();
      setDropRow(undefined);
      const file = event.dataTransfer.files[0];
      if (!file) return;
      if (!accepts(file)) fail(languageCode, "type");
      else upload(languageCode, file, replacing);
    },
  });

  const originalRow = (
    <EditorColumnRow
      key="original"
      role="listitem"
      tabIndex={-1}
      className="cursor-default"
      title={t("asset_localization.original")}
      titleClassName="font-normal"
      leading={
        <LanguageThumbnail
          variant={originalVariant}
          fileIcon={fileIcon}
          shape={thumbnail}
          onClick={!originalVariant ? onChangeOriginal : undefined}
          label={t("asset_localization.original")}
        />
      }
      trailing={
        <>
          {playButton(originalVariant)}
          {originalVariant && onChangeOriginal && (
            <RowMenu>
              <PiMenuButton
                text={changeOriginalLabel ?? t("change_bubble")}
                icon={<ImageSearchOutlinedIcon color="inherit" fontSize="inherit" />}
                onClick={onChangeOriginal}
                variant="modern"
              />
            </RowMenu>
          )}
        </>
      }
      testId="asset-language-row-original"
      dataAttributes={{ "data-has-variant": originalVariant ? "true" : "false" }}
    />
  );

  return (
    <div className="flex flex-col gap-0.5" role="list" data-testid={testId}>
      {originalRow}
      {referenceFirst(languages, referenceLanguage).map((language) => {
        const code = language.shortcode;
        const own = ownVariant(code);
        const state = uploads[code];
        const busy = state?.status === "uploading";
        // A language without its own image can take one: click anywhere on its row, or drop a file.
        // A language with one only takes a dropped file (replace) — a stray click must not overwrite.
        const canAdd = !own && !!asset && !busy;
        const canDrop = !!asset && !busy;
        const over = dropRow === code;

        return (
          <div key={code} {...(canDrop ? dropProps(code, own) : {})}>
            <EditorColumnRow
              {...(canAdd
                ? {
                    role: "button",
                    tabIndex: 0,
                    className: "group",
                    ariaLabel: t("asset_localization.upload_for", { language: language.full }) as string,
                    onClick: () => chooseFile({ languageCode: code }),
                    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        // Opened from inside Enter's keydown, Chromium drops the file picker (Space and
                        // clicks are fine). One tick later it opens; the user activation still holds.
                        if (event.key === "Enter") setTimeout(() => chooseFile({ languageCode: code }), 0);
                        else chooseFile({ languageCode: code });
                      }
                    },
                  }
                : { role: "listitem", tabIndex: -1, className: "cursor-default" })}
              selected={languageCodesEqual(code, currentLanguage?.shortcode)}
              dropTarget={over}
              title={language.full}
              titleClassName="font-normal"
              subtitle={<RowStatus t={t} own={!!own} state={state} invite={canAdd && isDraggingFile} over={over} />}
              leading={
                <LanguageThumbnail
                  variant={own ?? originalVariant}
                  fileIcon={fileIcon}
                  localUrl={state && "localUrl" in state ? state.localUrl : undefined}
                  progress={state?.status === "uploading" ? state.progress : undefined}
                  usesOriginal={!own && state?.status !== "added"}
                  invite={over || (canAdd && isDraggingFile)}
                  hoverHint={canAdd}
                  shape={thumbnail}
                  label={t("asset_localization.upload_for", { language: language.full })}
                />
              }
              trailing={
                <>
                  {!busy && playButton(own ?? originalVariant)}
                  {own && !busy && (
                    <RowMenu>
                      <PiMenuButton
                        text={t("asset_localization.replace")}
                        icon={<UploadOutlinedIcon color="inherit" fontSize="inherit" />}
                        onClick={() => chooseFile({ languageCode: code, replacing: own })}
                        variant="modern"
                      />
                    </RowMenu>
                  )}
                </>
              }
              testId={`asset-language-row-${code}`}
              dataAttributes={{ "data-has-variant": own ? "true" : "false" }}
            />
          </div>
        );
      })}

      <input
        ref={fileInput}
        type="file"
        accept={accept}
        className="hidden"
        data-testid="asset-language-file-input"
        onChange={(event) => {
          onFilePicked(event.target.files?.[0]);
          event.target.value = "";
        }}
      />


    </div>
  );
}

/** The reference language leads the languages; the others keep their order. */
const referenceFirst = (languages: LanguageData[], referenceLanguage: string | undefined) => {
  const index = languages.findIndex((l) => languageCodesEqual(l.shortcode, referenceLanguage));
  if (index <= 0) return languages;
  return [languages[index], ...languages.slice(0, index), ...languages.slice(index + 1)];
};

function RowMenu(props: { children: React.ReactNode }) {
  return (
    <span className="my-[-4px] flex-shrink-0" onClick={(e: MouseEvent) => e.stopPropagation()}>
      <PiPopoverMenu softIconHover modernMenu>
        {props.children}
      </PiPopoverMenu>
    </span>
  );
}

function RowStatus(props: { t: (key: string, options?: any) => any; own: boolean; state?: LanguageUpload; invite: boolean; over: boolean }) {
  const { t, own, state, invite, over } = props;
  if (state?.status === "uploading") return <span className="text-creator-primary">{t("asset_localization.uploading", { percent: state.progress })}</span>;
  if (state?.status === "failed") {
    const key = state.reason === "type" ? "wrong_file_type" : "upload_failed";
    return <span className="text-creator-error">{t(`asset_localization.${key}`)}</span>;
  }
  if (over) return <span className="text-creator-primary">{t(own ? "asset_localization.drop_to_replace" : "asset_localization.drop_to_add")}</span>;
  if (invite) return <span className="text-creator-primary">{t("asset_localization.drop_here")}</span>;
  return own || state?.status === "added" ? null : <>{t("asset_localization.uses_original")}</>;
}

function PlayButton(props: { playing: boolean; onToggle: () => void; t: (key: string) => any }) {
  const { playing, onToggle, t } = props;
  return (
    <button
      type="button"
      className="w-[26px] h-[26px] flex items-center justify-center rounded-full border-0 bg-transparent text-creator-text-sub hover:text-creator-primary hover:bg-creator-childHover cursor-pointer text-[20px]"
      aria-label={t(playing ? "asset_localization.stop" : "asset_localization.play") as string}
      aria-pressed={playing}
      // The row behind it may open the file picker on click.
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      onKeyDown={(event) => event.stopPropagation()}
      data-testid="asset-language-play"
    >
      {playing ? <StopRoundedIcon fontSize="inherit" /> : <PlayArrowRoundedIcon fontSize="inherit" />}
    </button>
  );
}

function LanguageThumbnail(props: {
  variant: AssetVariantDTO | undefined;
  /** No picture to show (audio, PDF): an icon instead. */
  fileIcon?: FileIcon;
  /** A file just picked for this language: shown until the server has encoded its thumbnail. */
  localUrl?: string;
  progress?: number;
  /** The language has no variant of its own: show the original, dashed. */
  usesOriginal?: boolean;
  /** A file is being dragged over the app: this box can take it. */
  invite?: boolean;
  /** The row around it is clickable: show the upload hint on hover. */
  hoverHint?: boolean;
  shape: "wide" | "square";
  label: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
}) {
  const { variant, fileIcon, localUrl, progress, usesOriginal, invite, hoverHint, shape, label, onClick } = props;
  const size = shape === "wide" ? "w-[64px] h-[32px]" : "w-[36px] h-[36px]";
  const src = localUrl ?? variant?.downloadUri;
  const uploading = progress !== undefined;

  const interactive = onClick
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": label,
        onClick,
        onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick(event as unknown as MouseEvent<HTMLElement>);
          }
        },
      }
    : {};

  if (!src) {
    return (
      <span
        {...interactive}
        className={classNames(
          size,
          "flex-shrink-0 rounded-5 border border-dashed border-creator-text-sub text-creator-text-sub flex items-center justify-center text-[16px]",
          onClick && "cursor-pointer hover:border-creator-primary hover:text-creator-primary"
        )}
        data-testid="asset-language-empty"
      >
        <AddRoundedIcon fontSize="inherit" color="inherit" />
      </span>
    );
  }

  return (
    <span
      {...interactive}
      className={classNames(
        size,
        "relative flex-shrink-0 rounded-5 overflow-hidden border bg-creator-childHover",
        usesOriginal || uploading ? "border-dashed" : "border-solid",
        invite ? "border-creator-primary" : usesOriginal ? "border-creator-text-sub" : "border-creator-border",
        (onClick || hoverHint) && "cursor-pointer group-hover:border-creator-primary"
      )}
      data-testid={usesOriginal ? "asset-language-uses-original" : undefined}
    >
      {fileIcon ? (
        <span
          className={classNames(
            "w-full h-full flex items-center justify-center text-[18px] text-creator-text-sub",
            (usesOriginal || uploading) && "opacity-60"
          )}
          data-testid={`asset-language-icon-${fileIcon}`}
        >
          {fileIcon === "video" ? (
            <MovieOutlinedIcon fontSize="inherit" />
          ) : fileIcon === "audio" ? (
            <MusicNoteOutlinedIcon fontSize="inherit" />
          ) : (
            <PictureAsPdfOutlinedIcon fontSize="inherit" />
          )}
        </span>
      ) : (
        <img src={src} alt="" className={classNames("block w-full h-full object-cover", (usesOriginal || uploading) && "opacity-60")} />
      )}
      {(onClick || hoverHint || invite) && (
        <span
          className={classNames(
            "absolute inset-0 flex items-center justify-center text-[16px] text-creator-primary bg-[color-mix(in_srgb,var(--pi-surface)_70%,transparent)] transition-opacity",
            invite ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
          )}
        >
          <UploadOutlinedIcon fontSize="inherit" color="inherit" />
        </span>
      )}
      {uploading && (
        <span className="absolute left-1 right-1 bottom-1 h-[3px] rounded-full bg-creator-border overflow-hidden" data-testid="asset-language-progress">
          <span className="block h-full bg-creator-primary" style={{ width: `${progress}%` }} />
        </span>
      )}
    </span>
  );
}
